import React, { useEffect, useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUserRoles,
  fetchAllPrivileges,
  fetchAllUsersRegisterd
} from "../redux/actions/userAction";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import axios from "axios";

function CompanyAccessManagement() {
  const dispatch = useDispatch();

  // Redux selectors
  const { users = [] } = useSelector((state) => state.users || {});
  const { userRoles = [] } = useSelector((state) => state.userRoles || {});
  const privileges = useSelector((state) =>
    Array.isArray(state.privileges?.privileges)
      ? state.privileges.privileges
      : Array.isArray(state.privileges?.results)
        ? state.privileges.results
        : []
  );

  // State
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // On mount
  useEffect(() => {
    dispatch(fetchAllUserRoles());
    dispatch(fetchAllPrivileges());
    dispatch(fetchAllUsersRegisterd());
  }, [dispatch]);

  // Lookups
  const roleIdToRole = {};
  userRoles.forEach(role => {
    roleIdToRole[role.id] = role;
  });
  const privilegeIdToObject = {};
  privileges.forEach(p => {
    privilegeIdToObject[p.id] = p;
  });

  // Selected user logic
  const selectedUser = users.find(u => String(u.id) === String(selectedUserId));
  const userRole = selectedUser && roleIdToRole[selectedUser.user_role]
    ? roleIdToRole[selectedUser.user_role]
    : null;

  // If just saved, show the new role's privileges, else show original
  const effectiveRole = (selectedRoleId && saveSuccess && selectedRoleId !== (userRole && userRole.id))
    ? roleIdToRole[selectedRoleId]
    : userRole;

  // Table data for single user
  const tableData = selectedUser
    ? [{
      id: selectedUser.id,
      username: selectedUser.username,
      name: `${selectedUser.first_name} ${selectedUser.last_name}`,
      email: selectedUser.email,
      role: effectiveRole ? effectiveRole.name : "No Role Assigned",
      privileges: effectiveRole && Array.isArray(effectiveRole.privilege_actions)
        ? effectiveRole.privilege_actions.map(
          priv =>
            (typeof priv === "object" && priv !== null)
              ? priv.name
              : (privilegeIdToObject[priv]?.name || priv)
        )
        : []
    }]
    : [];

  const privilegesBodyTemplate = (rowData) =>
    Array.isArray(rowData.privileges) && rowData.privileges.length
      ? rowData.privileges.join(", ")
      : <span className="text-muted">No privileges assigned</span>;

  // Save role for user handler
  const handleAssignRole = async () => {
    if (!selectedUserId || !selectedRoleId) return;
    setSaveLoading(true);
    setSaveSuccess(false);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/update/${selectedUserId}/`,
        { user_role: selectedRoleId }
      );
      setSaveSuccess(true);
      setSaveLoading(false);
      // Refresh/reload users so the current user's data updates
      dispatch(fetchAllUsersRegisterd());
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      setSaveLoading(false);
      setSaveSuccess(false);
      alert("Failed to save role for user.");
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4 text-center">User Access Management</h2>
      {/* Username dropdown */}
      <Form.Group className="mb-3">
        <Form.Label>Select User</Form.Label>
        <Form.Select
          value={selectedUserId}
          onChange={e => { setSelectedUserId(e.target.value); setSaveSuccess(false); }}
        >
          <option value="">-- Select a User --</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.username}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      {/* Show selected user info and privileges table */}
      {selectedUser && (
        <>
          <div className="card mb-3">
            <DataTable value={tableData} paginator rows={1} responsiveLayout="scroll">
              <Column field="id" header="User ID" />
              <Column field="username" header="Username" />
              <Column field="name" header="Name" />
              <Column field="email" header="Email" />
              <Column field="role" header="Role Name" />
              <Column header="Privileges" body={privilegesBodyTemplate} />
            </DataTable>
          </div>
          {/* User role selection dropdown */}
          <Form.Group className="mb-3">
            <Form.Label>Assign New User Role</Form.Label>
            <Form.Select
              value={selectedRoleId}
              onChange={e => { setSelectedRoleId(e.target.value); setSaveSuccess(false); }}
            >
              <option value="">-- Select User Role --</option>
              {userRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* Show selected role's privileges */}
          {selectedRoleId && roleIdToRole[selectedRoleId] && (
            <div className="mb-3">
              <strong>Selected Role: </strong> {roleIdToRole[selectedRoleId].name}<br />
              <strong>Privileges:</strong>{" "}
              {Array.isArray(roleIdToRole[selectedRoleId].privilege_actions) && roleIdToRole[selectedRoleId].privilege_actions.length ? (
                <span>
                  {roleIdToRole[selectedRoleId].privilege_actions.map(
                    priv =>
                      (typeof priv === "object" && priv !== null)
                        ? priv.name
                        : (privilegeIdToObject[priv]?.name || String(priv))
                  ).join(", ")}
                </span>
              ) : (
                <span className="text-muted">No privileges assigned</span>
              )}
            </div>
          )}

          {/* Save button */}
          <div className="mb-3">
            <Button
              variant="primary"
              disabled={!selectedUserId || !selectedRoleId || saveLoading}
              onClick={handleAssignRole}
            >
              {saveLoading ? "Saving..." : "Save"}
            </Button>
            {saveSuccess && (
              <span className="text-success ms-3">Role assigned successfully!</span>
            )}
          </div>
        </>
      )}
    </Container>
  );
}

export default CompanyAccessManagement;
